/**
 * Mention notification sounds.
 *
 * The user's preference is a sound ID (not a bare on/off) so we can grow the
 * set later — bundled presets, or owner-provided per-tenant sounds — without
 * changing the play path or the stored preference. Today only the synthesized
 * 'chime' exists; 'off' is the silent sentinel.
 *
 * The chime is generated with the Web Audio API (no asset file to bundle, works
 * offline). A single lazily-created AudioContext is reused; browsers gate audio
 * until a user gesture, so the first ding after a fresh load may be silent —
 * acceptable for a notification, and any later one (post-interaction) plays.
 */

// Valid mention-sound preference values. Mirrors the backend allowlist in
// backend/src/services/socketService.js (set-mention-sound handler).
export const MENTION_SOUNDS = ['chime', 'off'];
export const DEFAULT_MENTION_SOUND = 'chime';

let audioCtx = null;

function getCtx() {
  if (typeof window === 'undefined') return null;
  const Ctx = window.AudioContext || window.webkitAudioContext;
  if (!Ctx) return null;
  if (!audioCtx) {
    try {
      audioCtx = new Ctx();
    } catch {
      return null;
    }
  }
  return audioCtx;
}

// Peak gain at volume 100. The historical fixed level; scaled linearly by the
// caller's 0-100 volume so the default (no arg) is unchanged.
const PEAK_GAIN = 0.18;

// A short, pleasant two-note blip (E6 -> A6) with a quick decay envelope.
// `volume` is 0-100; the gain envelope peak scales linearly with it.
function playChime(volume = 100) {
  const ctx = getCtx();
  if (!ctx) return;
  // A suspended context (autoplay policy) can be resumed once we're inside a
  // user-gesture-derived call stack; best-effort.
  if (ctx.state === 'suspended' && ctx.resume) ctx.resume().catch(() => {});

  // Clamp to [0,100]; a 0 volume is silent so skip the work entirely.
  const v = Math.min(100, Math.max(0, Number(volume)));
  if (!Number.isFinite(v) || v <= 0) return;
  // exponentialRampToValueAtTime can't target 0, so the peak must stay > 0.
  const peak = Math.max(0.0002, PEAK_GAIN * (v / 100));

  const now = ctx.currentTime;
  const notes = [
    { freq: 1318.51, at: 0.0 },   // E6
    { freq: 1760.0, at: 0.09 },   // A6
  ];
  notes.forEach(({ freq, at }) => {
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.type = 'sine';
    osc.frequency.value = freq;
    // Pluck envelope: fast attack, exponential decay.
    const t0 = now + at;
    gain.gain.setValueAtTime(0.0001, t0);
    gain.gain.exponentialRampToValueAtTime(peak, t0 + 0.012);
    gain.gain.exponentialRampToValueAtTime(0.0001, t0 + 0.22);
    osc.connect(gain);
    gain.connect(ctx.destination);
    osc.start(t0);
    osc.stop(t0 + 0.24);
  });
}

/**
 * Play the mention sound identified by `id`. Unknown ids and 'off' are no-ops.
 * Safe to call frequently; never throws.
 *
 * @param {string} id      a value from MENTION_SOUNDS
 * @param {number} [volume] 0-100; defaults to full volume (historical level)
 */
export function playMentionSound(id, volume = 100) {
  try {
    switch (id) {
      case 'chime':
        playChime(volume);
        break;
      case 'off':
      default:
        break;
    }
  } catch {
    /* never let a notification sound break the message pipeline */
  }
}
